import { useState, type FormEvent } from 'react';
import { Cabin } from '../components/Cabin';
import { Dialogue } from '../components/Dialogue';
import { Panel } from '../components/Panel';
import { PixelButton } from '../components/PixelButton';
import { Triangle } from '../components/Triangle';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type Mode = 'login' | 'signup' | 'forgot';

type Props = {
  initialMode?: Mode;
  onBack?: () => void;
};

export function AuthScreen({ initialMode = 'login', onBack }: Props = {}) {
  const { signUp, logIn } = useAuth();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      if (mode === 'signup') await signUp(email.trim().toLowerCase(), password);
      else if (mode === 'forgot') {
        await api.forgotPassword(email.trim().toLowerCase());
        setSent(true);
      } else await logIn(email.trim().toLowerCase(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  };

  const go = (next: Mode) => {
    setMode(next);
    setError(null);
    setSent(false);
  };

  const isSignup = mode === 'signup';
  const isForgot = mode === 'forgot';
  const title = isSignup ? 'NEW QUEST LOG' : isForgot ? 'LOST THE KEY?' : 'WELCOME BACK';

  return (
    <div className="auth-stage">
      <div className="auth-brand"><Cabin size={34} /><span>HABIT HOLLOW</span></div>
      <div className="auth-sub">a small 16-bit habit log</div>

      <Panel title={title} bodyClassName="panel-body auth-body">
        <Dialogue speaker="MAYOR" style={{ marginBottom: 18 }}>
          {isSignup
            ? "Oh, a new face! Come on in — we'll get you in the ledger, no fuss."
            : isForgot
              ? "Mislaid your key? Happens to the best of us. Tell me the name on your ledger and I'll post you a fresh one."
              : "Back again, eh? Good, good. Journal's on the shelf."}
        </Dialogue>

        {isForgot && sent ? (
          <>
            <Dialogue speaker="MAYOR" style={{ marginBottom: 18 }}>
              If that name's in our ledger, a letter with a fresh key is already on its way.
              Do peek in the spam drawer if it's shy — the link rests after an hour.
            </Dialogue>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6 }}>
              <PixelButton variant="ghost" type="button" onClick={() => go('login')}>
                <Triangle direction="left" /> BACK TO LOG IN
              </PixelButton>
            </div>
          </>
        ) : (
          <form onSubmit={submit} noValidate>
            <div className="field">
              <div className="field__label">EMAIL</div>
              <input
                className="pixel-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="wanderer@realm.net"
                autoFocus
                required
              />
            </div>

            {!isForgot && (
              <div className="field">
                <div className="field__label">
                  PASSWORD {isSignup && <span style={{ opacity: 0.6 }}>· 8+ CHARS</span>}
                </div>
                <input
                  className="pixel-input"
                  type="password"
                  autoComplete={isSignup ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={isSignup ? 8 : 1}
                />
              </div>
            )}

            {error && <div className="auth-error">▸ {error}</div>}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 14 }}>
              {isForgot ? (
                <PixelButton variant="ghost" type="button" onClick={() => go('login')}>
                  <Triangle direction="left" /> BACK
                </PixelButton>
              ) : (
                <PixelButton variant="ghost" type="button" onClick={() => go(isSignup ? 'login' : 'signup')}>
                  {isSignup ? (
                    <><Triangle direction="left" /> HAVE ACCOUNT</>
                  ) : (
                    <>NEW? SIGN UP <Triangle direction="right" /></>
                  )}
                </PixelButton>
              )}
              <PixelButton variant="gold" type="submit" disabled={pending}>
                {pending
                  ? (isSignup ? 'FORGING…' : isForgot ? 'SENDING…' : 'ENTERING…')
                  : (
                    <><Triangle /> {isSignup ? 'BEGIN' : isForgot ? 'SEND KEY' : 'LOG IN'}</>
                  )}
              </PixelButton>
            </div>

            {mode === 'login' && (
              <div className="auth-forgot">
                <button type="button" className="auth-link" onClick={() => go('forgot')}>
                  Lost your key?
                </button>
              </div>
            )}
          </form>
        )}
      </Panel>

      {onBack && (
        <div className="auth-back">
          <PixelButton variant="ghost" type="button" onClick={onBack}>
            <Triangle direction="left" /> BACK TO THE HOLLOW
          </PixelButton>
        </div>
      )}

      <div className="auth-footnote">
        Your data is yours alone. No one else can see your quests.
      </div>
    </div>
  );
}
